import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * Staffing Overview Component
 *
 * Shows healthcare staffing distribution with stat cards and a bar chart
 */
function StaffingOverview({ data, title, narrative, templateText }) {
  if (!data || !data.distribution || data.distribution.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">No staffing data available.</p>
      </div>
    )
  }

  // Top 4 staff categories for stat cards
  const topStaff = [...data.distribution]
    .sort((a, b) => b.value - a.value)
    .slice(0, 4)

  // Sort data for bar chart (descending)
  const sortedData = [...data.distribution].sort((a, b) => b.value - a.value)

  // Create bar chart option
  const chartOption = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const item = params[0]
        const staff = sortedData[item.dataIndex]
        return `
          <b>${staff.name}</b><br/>
          Total: ${staff.value}<br/>
          Average per facility: ${staff.average.toFixed(1)}
        `
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    grid: {
      left: '25%',
      right: '10%',
      top: '5%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: 'Total Staff',
      nameTextStyle: {
        fontSize: 12,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      axisLabel: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      type: 'category',
      data: sortedData.map(s => s.name),
      axisLabel: {
        fontSize: 11,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Staff Count',
        type: 'bar',
        data: sortedData.map(s => ({
          value: s.value,
          itemStyle: { color: '#3b82f6' }, // Blue color
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}',
          fontSize: 11,
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        barMaxWidth: 30,
      },
    ],
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

      {/* Narrative or template text */}
      {(narrative || templateText) && (
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            {narrative || templateText}
          </p>
        </div>
      )}

      {/* Top 4 Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {topStaff.map((staff, index) => (
          <div
            key={index}
            className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4"
          >
            <div className="text-3xl mb-1">{staff.icon}</div>
            <div className="text-2xl font-bold text-gray-900">
              {staff.value}
            </div>
            <div className="text-xs text-gray-600 mt-1">{staff.name}</div>
            <div className="text-xs text-gray-500 mt-1">
              Avg: {staff.average.toFixed(1)} per facility
            </div>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      <div className="h-96">
        <ReactECharts
          option={chartOption}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          theme="ug-dashboard"
        />
      </div>
    </div>
  )
}

StaffingOverview.propTypes = {
  data: PropTypes.shape({
    totals: PropTypes.object.isRequired,
    averages: PropTypes.object.isRequired,
    distribution: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        average: PropTypes.number.isRequired,
        icon: PropTypes.string,
        category: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  narrative: PropTypes.string,
  templateText: PropTypes.string,
}

export default StaffingOverview
